import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Attach JWT on every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('futurum_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;
    if (response?.status === 401 && response?.data?.code === 'TOKEN_EXPIRED') {
      localStorage.removeItem('futurum_token');
      localStorage.removeItem('futurum_user');
      window.location.href = '/login?expired=1';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Topics
export const topicsAPI = {
  getAll: (params) => api.get('/topics', { params }),
  getOne: (id) => api.get(`/topics/${id}`),
  getMy: () => api.get('/topics/my'),
  getDomains: () => api.get('/topics/domains'),
  create: (data) => api.post('/topics', data),
  update: (id, data) => api.put(`/topics/${id}`, data),
  getApplications: (id) => api.get(`/topics/${id}/applications`),
};

// Applications
export const applicationsAPI = {
  apply: (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => v != null && form.append(k, v));
    if (file) form.append('document', file);
    return api.post('/applications', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getMy: () => api.get('/applications/my'),
  review: (id, data) => api.put(`/applications/${id}/review`, data),
  submitIndependent: (data, file) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => v != null && form.append(k, v));
    if (file) form.append('document', file);
    return api.post('/applications/independent', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Sponsorships
export const sponsorshipsAPI = {
  create: (data) => api.post('/sponsorships', data),
  getMy: () => api.get('/sponsorships/my'),
  getApplicants: (id) => api.get(`/sponsorships/${id}/applicants`),
  selectAwardee: (id, data) => api.put(`/sponsorships/${id}/select`, data),
  delete: (id) => api.delete(`/sponsorships/${id}`),
  uploadReceipt: (formData) => api.post('/sponsorships/receipt/bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Projects
export const projectsAPI = {
  inviteSupervisor: (projectId, lecturerId) => api.post('/projects/' + projectId + '/invite-supervisor', { lecturer_id: lecturerId }),
  respondToSupervisorInvitation: (projectId, invitationId, action) => api.put('/projects/' + projectId + '/supervisor-invitation/' + invitationId, { action }),
  getSupervisorInvitation: (projectId) => api.get('/projects/' + projectId + '/supervisor-invitation'),
  getLecturerInvitations: () => api.get('/lecturer/supervisor-invitations'),
  toggleShowcase: (id, enabled) => api.put(`/projects/${id}/showcase`, { enabled }),
  updateShowcaseProfile: (id, data) => api.put(`/projects/${id}/showcase-profile`, data),
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  updateStatus: (id, status) => api.put(`/projects/${id}/status`, { status }),
  createMilestone: (id, data) => api.post(`/projects/${id}/milestones`, data),
  updateMilestone: (projectId, milestoneId, data) => api.put(`/projects/${projectId}/milestones/${milestoneId}`, data),
  addMilestonePhoto: (projectId, milestoneId, form) => api.post(`/projects/${projectId}/milestones/${milestoneId}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  submit: (id, data, files) => {
    const form = new FormData();
    if (data.summary) form.append('summary', data.summary);
    files?.forEach(f => form.append('files', f));
    return api.post(`/projects/${id}/submit`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getMyEntryOptions: () => api.get('/projects/my-entry-options'),
  enterFinale: (entryType, entryId, deckFile, freeTitle) => {
    const form = new FormData();
    form.append('entry_type', entryType);
    if (entryId) form.append('entry_id', entryId);
    if (freeTitle) form.append('title', freeTitle);
    form.append('entry_deck', deckFile);
    return api.post('/projects/enter-finale', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  addEntryImages: (projectId, imageFiles) => {
    const form = new FormData();
    imageFiles.forEach(f => form.append('entry_images', f));
    return api.post(`/projects/${projectId}/entry-images`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeEntryImage: (projectId, path) => api.delete(`/projects/${projectId}/entry-images`, { data: { path } }),
};

// Profiles
export const profilesAPI = {
  getMy: () => api.get('/profiles/me'),
  updateMy: (data) => {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v != null) {
        if (Array.isArray(v)) form.append(k, JSON.stringify(v));
        else form.append(k, v);
      }
    });
    return api.put('/profiles/me', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getUser: (id) => api.get(`/profiles/user/${id}`),
};

// Admin
export const adminAPI = {
  getEventRegistrations: () => api.get('/admin/event-registrations'),
  confirmCheckin: (reference) => api.post(`/admin/checkin/${reference}`),
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUserStatus: (id, data) => api.put(`/admin/users/${id}/status`, data),
  getPendingTopics: () => api.get('/admin/topics/pending'),
  approveTopic: (id, data) => api.put(`/admin/topics/${id}/approve`, data),
  getConfig: () => api.get('/admin/config'),
  updateConfig: (updates) => api.put('/admin/config', { updates }),
  assignJudge: (data) => api.post('/admin/judges/assign', data),
  updateJudgeProfile: (id, formData) => api.put(`/admin/judges/${id}/profile`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  publishResults: (data) => api.post('/admin/results/publish', data),
  getAnalytics: () => api.get('/admin/analytics'),
  getPendingReceipts: () => api.get('/admin/sponsorships/receipts'),
  verifyReceipt: (id, data) => api.put(`/admin/sponsorships/${id}/verify`, data),

  // Social Media Agent
  getSocialPosts: (params) => api.get('/admin/social/posts', { params }),
  generateSocialPost: (data) => api.post('/admin/social/generate', data),
  approveSocialPost: (id, data) => api.put('/admin/social/posts/' + id + '/approve', data),
  rejectSocialPost: (id) => api.put('/admin/social/posts/' + id + '/reject'),
  deleteSocialPost: (id) => api.delete('/admin/social/posts/' + id),
  updateSocialPost: (id, data) => api.put('/admin/social/posts/' + id, data),
  getLinkedInStatus: () => api.get('/admin/linkedin/status'),
  uploadSocialImage: (id, formData) => api.post('/admin/social/posts/' + id + '/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Judging
export const judgingAPI = {
  getAssigned: () => api.get('/judging/assigned'),
  getProject: (id) => api.get(`/judging/projects/${id}`),
  submitScore: (data) => api.post('/judging/scores', data),
  getRankings: () => api.get('/judging/rankings'),
  declareCOI: (projectId, data) => api.post(`/judging/coi/${projectId}`, data),
  getCOIDeclarations: () => api.get('/judging/coi'),
};

// Notifications
export const departmentsAPI = {
  getAll: () => api.get('/departments'),
  getPublic: () => api.get('/public/departments'),
  getBankDetails: (id) => api.get(`/departments/${id}/bank`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Public
export const publicAPI = {
  getTopics: (params) => api.get('/public/topics', { params }),
  getDomains: () => api.get('/public/domains'),
  getShowcase: (slug) => api.get(`/public/showcase/${slug}`),
  getStats: () => api.get('/public/stats'),
  getWinners: () => api.get('/public/winners'),
  getSponsors: () => api.get('/public/sponsors'),
  getJudges:  () => api.get('/public/judges'),
  getConfig: () => api.get('/public/config'),
};

export const renewalAPI = {
  sendEmails:   ()      => api.post('/admin/renewal/send'),
  getIntents:   ()      => api.get('/admin/renewal/intents'),
  getPage:      (token) => api.get(`/public/renew/${token}`),
  confirmIntent:(token, data) => api.post(`/public/renew/${token}`, data),
};

export default api;

// Certificates
export const certificatesAPI = {
  getMy: () => api.get('/certificates/my'),
  download: (certId) => api.get(`/certificates/${certId}/download`, { responseType: 'blob' }),
  verify: (certId) => api.get(`/public/verify/${certId}`),
  bulkIssue: () => api.post('/admin/certificates/bulk-issue'),
};

// Voting (public)
export const votingPublicAPI = {
  getActive: () => api.get('/public/poll'),
  castVote: (data) => api.post('/public/poll/vote', data),
  getResults: (pollId) => api.get(`/public/poll/${pollId}/results`),
};

// Voting (admin)
export const votingAdminAPI = {
  getAll: () => api.get('/admin/polls'),
  create: (data) => api.post('/admin/polls', data),
  updateStatus: (id, data) => api.put(`/admin/polls/${id}/status`, data),
  remove: (id) => api.delete(`/admin/polls/${id}`),
};

// Feedback (public)
export const feedbackPublicAPI = {
  getActive: () => api.get('/public/feedback'),
  submit: (data) => api.post('/public/feedback', data),
};

// Feedback (admin)
export const feedbackAdminAPI = {
  getAll: () => api.get('/admin/feedback-forms'),
  create: (data) => api.post('/admin/feedback-forms', data),
  getDetail: (id) => api.get(`/admin/feedback-forms/${id}`),
  update: (id, data, force = false) => api.put(`/admin/feedback-forms/${id}${force ? '?force=true' : ''}`, data),
  updateStatus: (id, data) => api.put(`/admin/feedback-forms/${id}/status`, data),
  getResponses: (id) => api.get(`/admin/feedback-forms/${id}/responses`),
  exportCSV: (id) => api.get(`/admin/feedback-forms/${id}/responses?format=csv`, { responseType: 'blob' }),
  remove: (id) => api.delete(`/admin/feedback-forms/${id}`),
};
